import type { ResourceTimingSlice } from './types';

/**
 * How many finished entries are kept per URL.
 *
 * The dashboard poll hits the same URL every ten seconds, so a single URL can
 * have several entries in flight across one flush window. Keeping a few and
 * taking the oldest first matches them up in order without unbounded growth.
 */
const MAX_ENTRIES_PER_URL = 8;

/** Entries older than this are discarded; nothing can still want them. */
const ENTRY_TTL_MS = 120_000;

/** A running observer. */
export interface ResourceTimingReader {
  /**
   * Take the oldest unclaimed measurement for a URL, removing it.
   *
   * Taking rather than peeking means two calls to the same URL get their own
   * entries instead of both reading the first one.
   */
  take: (url: string) => ResourceTimingSlice | undefined;
  /** Stop observing. */
  stop: () => void;
}

/**
 * Watch the browser's Resource Timing entries so calls can be broken into
 * connection setup, server time and download.
 *
 * A PerformanceObserver is used rather than `performance.getEntriesByName`
 * because the browser's resource buffer holds only about 250 entries and
 * silently drops the rest. A Connect tab left open with a ten-second poll
 * overflows that within minutes, after which `getEntriesByName` returns nothing
 * and the breakdown quietly stops working. An observer receives each entry as
 * it completes, so the buffer's size stops mattering.
 *
 * Returns a reader that no-ops where PerformanceObserver is unavailable, so
 * callers never need to feature-detect.
 */
export function createResourceTimingReader(): ResourceTimingReader {
  const byUrl = new Map<string, Array<{ at: number; timings: ResourceTimingSlice }>>();

  if (typeof PerformanceObserver === 'undefined') {
    return { take: () => undefined, stop: () => {} };
  }

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.entryType !== 'resource') {
        continue;
      }
      remember(byUrl, entry as PerformanceResourceTiming);
    }
  });

  try {
    // buffered picks up entries from before this ran, which covers the calls an
    // app makes while it is still starting up.
    observer.observe({ type: 'resource', buffered: true });
  } catch {
    // Older browsers reject the options form. Without the observer there is no
    // breakdown, which the record shape already tolerates.
    return { take: () => undefined, stop: () => {} };
  }

  return {
    take(url) {
      const key = normalizeUrl(url);
      const entries = byUrl.get(key);
      if (!entries || entries.length === 0) {
        return undefined;
      }

      const oldest = entries.shift();
      if (entries.length === 0) {
        byUrl.delete(key);
      }

      return oldest?.timings;
    },
    stop() {
      observer.disconnect();
      byUrl.clear();
    },
  };
}

/** Record one entry, evicting stale and excess ones as we go. */
function remember(
  byUrl: Map<string, Array<{ at: number; timings: ResourceTimingSlice }>>,
  entry: PerformanceResourceTiming,
): void {
  const key = normalizeUrl(entry.name);
  const now = Date.now();

  const entries = (byUrl.get(key) ?? []).filter((held) => now - held.at < ENTRY_TTL_MS);
  entries.push({ at: now, timings: toSlice(entry) });

  byUrl.set(key, entries.slice(-MAX_ENTRIES_PER_URL));
}

/**
 * Pull the phases out of an entry.
 *
 * Cross-origin these are all 0 unless the server sent Timing-Allow-Origin. The
 * zeros are passed through as-is and the collector drops them, so the "not
 * allowed to see it" case is handled in exactly one place.
 */
function toSlice(entry: PerformanceResourceTiming): ResourceTimingSlice {
  return {
    ttfbMs: entry.responseStart - entry.requestStart,
    downloadMs: entry.responseEnd - entry.responseStart,
    connectMs: entry.connectEnd - entry.connectStart,
    dnsMs: entry.domainLookupEnd - entry.domainLookupStart,
    transferSize: entry.transferSize,
    encodedBodySize: entry.encodedBodySize,
  };
}

/**
 * Key entries by path, ignoring origin and query string.
 *
 * Entry names are absolute URLs; the app looks up by whatever it passed axios,
 * which may be relative. Matching on the path is what makes the two meet.
 */
function normalizeUrl(url: string): string {
  const withoutQuery = url.split('?')[0].split('#')[0];
  const schemeEnd = withoutQuery.indexOf('://');
  if (schemeEnd === -1) {
    return withoutQuery;
  }
  const afterHost = withoutQuery.indexOf('/', schemeEnd + 3);
  return afterHost === -1 ? '/' : withoutQuery.slice(afterHost);
}
